import { audioEvents, type AudioEvent } from '../../domain/chess/audioEvent';
export const soundFiles: Record<AudioEvent, string> = {
  move: '/vendor/chesscom/sounds/move-self.mp3',
  capture: '/vendor/chesscom/sounds/capture.mp3',
  check: '/vendor/chesscom/sounds/move-check.mp3',
  castle: '/vendor/chesscom/sounds/castle.mp3',
  promotion: '/vendor/chesscom/sounds/promote.mp3',
  checkmate: '/vendor/chesscom/sounds/game-end.mp3',
  incorrect: '/audio/incorrect.wav',
  'correct-step': '/audio/correct-step.wav',
  solved: '/audio/solved.wav',
};
export class AudioService {
  private context?: AudioContext;
  private gain?: GainNode;
  private buffers = new Map<AudioEvent, AudioBuffer>();
  private preloadPromise?: Promise<void>;
  private disposed = false;
  private sources = new Set<AudioBufferSourceNode>();
  private muted = false;
  private volume = 0.35;
  private pending = 0;
  private overOtherAudio = false;
  private playbackSession?: { session: { type: string }; previous: string; applied: string };
  private configurePlayback() {
    if (this.muted || this.volume === 0 || this.playbackSession) return;
    // Set routing before constructing Web Audio. Ambient mixes with other audio.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) {
      try {
        const previous = session.type;
        const applied = this.overOtherAudio ? 'playback' : 'ambient';
        session.type = applied;
        this.playbackSession = { session, previous, applied };
      } catch {
        // Unsupported session policies must not disable ordinary Web Audio.
      }
    }
  }
  private releasePlayback() {
    if (!this.playbackSession) return;
    try {
      const { session, previous, applied } = this.playbackSession;
      if (session.type === applied) session.type = previous;
    } catch {
      /* The platform owns the audio session. */
    }
    this.playbackSession = undefined;
  }
  async unlock() {
    if (this.disposed || this.muted || this.volume === 0) return;
    try {
      this.configurePlayback();
      if (!this.context || this.context.state === 'closed') {
        const context = new AudioContext({ latencyHint: 'interactive' });
        try {
          const gain = context.createGain();
          gain.gain.value = this.volume;
          gain.connect(context.destination);
          this.context = context;
          this.gain = gain;
        } catch (error) {
          this.close(context);
          throw error;
        }
      }
      const preload = this.preload();
      // iOS can enter "interrupted" after a call, screen lock or app switch.
      if (this.context.state !== 'running' && this.context.state !== 'closed')
        await this.context.resume();
      await preload;
    } catch {
      this.releasePlayback();
      /* Audio failure never blocks chess. */
    }
  }
  async preload() {
    if (this.preloadPromise) return this.preloadPromise;
    const context = this.context;
    if (!context) return;
    this.preloadPromise = Promise.all(
      audioEvents
        .filter((event) => !this.buffers.has(event))
        .map(async (event) => {
          try {
            const response = await fetch(soundFiles[event], { signal: AbortSignal.timeout(5000) });
            if (!response.ok) return;
            const buffer = await context.decodeAudioData(await response.arrayBuffer());
            if (!this.disposed) this.buffers.set(event, buffer);
          } catch {
            /* Silent degradation. */
          }
        }),
    )
      .then(() => {})
      .finally(() => {
        this.preloadPromise = undefined;
      });
    return this.preloadPromise;
  }
  play(event: AudioEvent) {
    const token = ++this.pending;
    if (this.disposed || this.muted || this.volume === 0 || document.hidden) return;
    if (this.start(event)) return;
    const at = performance.now();
    // Keep only the latest fresh cue while a first touch unlocks/loads audio.
    void this.unlock().then(() => {
      if (token === this.pending && performance.now() - at < 750) this.start(event);
    });
  }
  cancelPending() {
    this.pending++;
  }
  async preview(): Promise<boolean> {
    const token = ++this.pending;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const ready = await Promise.race([
        this.unlock().then(() => true),
        new Promise<false>((resolve) => {
          timeout = setTimeout(() => resolve(false), 5500);
        }),
      ]);
      return ready && token === this.pending && this.start('move');
    } catch {
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }
  private start(event: AudioEvent): boolean {
    const buffer = this.buffers.get(event);
    if (
      this.disposed ||
      this.muted ||
      this.volume === 0 ||
      document.hidden ||
      !buffer ||
      !this.context ||
      !this.gain ||
      this.context.state !== 'running'
    )
      return false;
    let source: AudioBufferSourceNode | undefined;
    try {
      source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.gain);
      this.sources.add(source);
      const playing = source;
      source.onended = () => this.releaseSource(playing);
      source.start();
      return true;
    } catch {
      if (source) this.releaseSource(source);
      return false;
    }
  }
  private releaseSource(source: AudioBufferSourceNode) {
    this.sources.delete(source);
    source.onended = null;
    try {
      source.disconnect();
    } catch {
      /* The platform may already have disconnected the source. */
    }
  }
  private close(context: AudioContext) {
    try {
      void context.close().catch(() => {});
    } catch {
      /* A suspended or lost device may reject close synchronously. */
    }
  }
  settings(muted: boolean, volume: number, overOtherAudio = false) {
    if (this.overOtherAudio !== overOtherAudio) this.releasePlayback();
    this.overOtherAudio = overOtherAudio;
    this.muted = muted;
    this.volume = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 0;
    if (this.gain && this.context) {
      try {
        this.gain.gain.cancelScheduledValues(this.context.currentTime);
        this.gain.gain.setValueAtTime(muted ? 0 : this.volume, this.context.currentTime);
      } catch {
        /* Settings still apply if the device has disconnected. */
      }
    }
    if (muted || this.volume === 0) {
      this.cancelPending();
      this.releasePlayback();
      for (const source of this.sources) {
        try {
          source.stop();
        } catch {
          /* Already ended. */
        }
        this.releaseSource(source);
      }
    } else if (this.context) {
      this.configurePlayback();
    }
  }
  get debug() {
    return {
      unlocked: this.context?.state === 'running',
      loaded: this.buffers.size,
      muted: this.muted,
      volume: this.volume,
      disposed: this.disposed,
    };
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelPending();
    this.releasePlayback();
    this.buffers.clear();
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        /* Already ended. */
      }
      this.releaseSource(s);
    }
    if (this.context) this.close(this.context);
    this.context = undefined;
    this.gain = undefined;
  }
}
